// Bloqueo de scroll del body con conteo de referencias (ref-count).
//
// Problema que resuelve: varios modales/drawers (BottomSheet, Drawer,
// RulebookAcceptanceModal, etc.) escribían `document.body.style.overflow`
// cada uno por su cuenta, con estrategias de restauración distintas (algunos
// guardaban el valor previo, otros forzaban `'unset'`). Cuando dos se
// superponían —cosa habitual en el dashboard de pretemporada, donde pueden
// convivir el gate del reglamento, las notificaciones y el modal de
// comprobante— se pisaban entre sí y el body podía quedar permanentemente en
// `overflow: hidden`. Resultado: la página no scrollea y, en pantallas chicas,
// el botón "Subir Comprobante" queda fuera de alcance.
//
// Solución: un único contador global. Solo el PRIMER lock guarda el valor
// original del body y lo cambia a `hidden`; solo cuando se libera el ÚLTIMO
// lock se restaura el valor original. Cualquier anidamiento/superposición de
// modales compone correctamente.

import { useEffect } from 'react';

let lockCount = 0;
let originalOverflow: string | null = null;

/** Incrementa el contador y bloquea el scroll del body si es el primer lock. */
export function lockBodyScroll(): void {
  if (typeof document === 'undefined') return;
  if (lockCount === 0) {
    originalOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
  }
  lockCount += 1;
}

/** Decrementa el contador y restaura el scroll del body si fue el último lock. */
export function unlockBodyScroll(): void {
  if (typeof document === 'undefined') return;
  if (lockCount === 0) return;
  lockCount -= 1;
  if (lockCount === 0) {
    document.body.style.overflow = originalOverflow ?? '';
    originalOverflow = null;
  }
}

/**
 * Hook: bloquea el scroll del body mientras `active` sea true y lo libera al
 * desmontar o cuando `active` pase a false. Seguro ante superposición de
 * modales gracias al ref-count compartido.
 */
export function useBodyScrollLock(active: boolean): void {
  useEffect(() => {
    if (!active) return;
    lockBodyScroll();
    return () => {
      unlockBodyScroll();
    };
  }, [active]);
}
